import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Megaphone,
  Zap,
  Star,
  Crown,
  Sparkles,
  Check,
  Eye,
  MousePointer,
  Clock,
  ArrowRight,
  Loader2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const PACKAGE_ICONS = {
  basic: Zap,
  standard: Star,
  premium: Crown,
  featured: Sparkles,
};

const PACKAGE_COLORS = {
  basic: "from-blue-500 to-blue-600",
  standard: "from-amber-500 to-amber-600",
  premium: "from-purple-500 to-purple-600",
  featured: "from-primary to-destructive",
};

export default function Ads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [packages, setPackages] = useState({});
  const [myAds, setMyAds] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, adsRes, postsRes] = await Promise.all([
        axios.get(`${API}/ads/packages`),
        axios.get(`${API}/ads/my-ads`),
        axios.get(`${API}/users/${user?.id}/posts`),
      ]);
      setPackages(packagesRes.data);
      setMyAds(adsRes.data);
      setMyPosts(postsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;

    try {
      const response = await axios.get(`${API}/ads/status/${sessionId}`);
      setPaymentResult(response.data);
      setSuccessDialogOpen(true);
      
      // Refresh ads list
      const adsRes = await axios.get(`${API}/ads/my-ads`);
      setMyAds(adsRes.data);
    } catch (error) {
      console.error("Failed to check payment status");
    }
  };

  const handleBoostPost = async () => {
    if (!selectedPost || !selectedPackage) return;

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/ads/checkout`, {
        post_id: selectedPost,
        package_id: selectedPackage,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout");
      setCheckoutLoading(false);
    }
  };

  const activeAds = myAds.filter((ad) => ad.status === "active");
  const pendingAds = myAds.filter((ad) => ad.status === "pending");
  const completedAds = myAds.filter((ad) => ad.status === "completed");

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/10 to-primary/10"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Boost Your Positivity
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Promote your positive posts to reach more people and spread happiness further
            </p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pricing packages */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Boost Packages</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(packages).map(([id, pkg]) => {
              const Icon = PACKAGE_ICONS[id] || Zap;
              const gradientClass = PACKAGE_COLORS[id] || PACKAGE_COLORS.basic;
              const isSelected = selectedPackage === id;

              return (
                <div
                  key={id}
                  onClick={() => setSelectedPackage(id)}
                  className={`card-happy cursor-pointer transition-all hover:scale-105 ${
                    isSelected ? "ring-2 ring-primary" : ""
                  }`}
                  data-testid={`package-${id}`}
                >
                  <div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradientClass} flex items-center justify-center mb-4`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold mb-1">{pkg.name}</h3>
                  <div className="text-3xl font-bold text-foreground mb-2">
                    ${pkg.price}
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{pkg.duration_days} day{pkg.duration_days > 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      <span>Up to {pkg.reach}</span>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="mt-4 flex items-center gap-2 text-primary">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Selected</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Select post to boost */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Select Post to Boost</h2>
          {myPosts.length === 0 ? (
            <div className="card-happy text-center py-12">
              <p className="text-muted-foreground mb-4">
                You don't have any posts to boost yet
              </p>
              <Button onClick={() => navigate("/create")} className="btn-primary">
                Create a Post
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myPosts.slice(0, 9).map((post) => {
                const isSelected = selectedPost === post.id;
                const isAlreadyBoosted = myAds.some(
                  (ad) => ad.post_id === post.id && ad.status === "active"
                );

                return (
                  <div
                    key={post.id}
                    onClick={() => !isAlreadyBoosted && setSelectedPost(post.id)}
                    className={`card-happy cursor-pointer transition-all ${
                      isSelected ? "ring-2 ring-primary" : ""
                    } ${isAlreadyBoosted ? "opacity-50 cursor-not-allowed" : "hover:shadow-hover"}`}
                    data-testid={`post-select-${post.id}`}
                  >
                    <p className="text-sm line-clamp-3 mb-3">{post.content}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {post.hearts} ❤️ {post.stars} ⭐ {post.hugs} 🤗
                      </span>
                      {isAlreadyBoosted && (
                        <Badge className="bg-primary/10 text-primary text-xs">
                          Boosted
                        </Badge>
                      )}
                    </div>
                    {isSelected && !isAlreadyBoosted && (
                      <div className="mt-3 flex items-center gap-2 text-primary">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">Selected</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Boost button */}
        {selectedPost && selectedPackage && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40">
            <Button
              onClick={handleBoostPost}
              disabled={checkoutLoading}
              data-testid="boost-now-btn"
              className="btn-primary text-lg px-8 py-6 shadow-2xl"
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Boost Now - ${packages[selectedPackage]?.price}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* My Ads */}
        {myAds.length > 0 && (
          <section>
            <h2 className="text-2xl font-semibold mb-6">Your Promotions</h2>
            <div className="space-y-4">
              {/* Active */}
              {activeAds.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    Active ({activeAds.length})
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {activeAds.map((ad) => (
                      <AdCard key={ad.id} ad={ad} />
                    ))}
                  </div>
                </div>
              )}

              {/* Pending */}
              {pendingAds.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                    Pending ({pendingAds.length})
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {pendingAds.map((ad) => (
                      <AdCard key={ad.id} ad={ad} />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed */}
              {completedAds.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>
                    Completed ({completedAds.length})
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {completedAds.map((ad) => (
                      <AdCard key={ad.id} ad={ad} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {paymentResult?.status === "paid" ? (
                <>
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  Payment Successful!
                </>
              ) : (
                <>
                  <XCircle className="w-6 h-6 text-red-500" />
                  Payment Issue
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {paymentResult?.status === "paid" ? (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-10 h-10 text-green-500" />
                </div>
                <p className="text-lg mb-2">Your post is now being promoted!</p>
                <p className="text-muted-foreground">
                  Amount paid: ${paymentResult?.amount}
                </p>
              </div>
            ) : (
              <p className="text-center text-muted-foreground">
                There was an issue with your payment. Please try again.
              </p>
            )}
          </div>
          <Button
            onClick={() => setSuccessDialogOpen(false)}
            className="w-full btn-primary"
          >
            Continue
          </Button>
        </DialogContent>
      </Dialog>

      <Navigation />
    </div>
  );
}

function AdCard({ ad }) {
  const Icon = PACKAGE_ICONS[ad.package_id] || Zap;

  return (
    <div className="card-happy" data-testid={`ad-card-${ad.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold">{ad.package_name}</h4>
            <p className="text-sm text-muted-foreground">${ad.amount}</p>
          </div>
        </div>
        <Badge
          variant={ad.status === "active" ? "default" : "secondary"}
          className={ad.status === "active" ? "bg-green-500" : ""}
        >
          {ad.status}
        </Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 py-3 border-t border-border">
        <div className="text-center">
          <div className="text-2xl font-bold">{ad.impressions}</div>
          <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <Eye className="w-3 h-3" />
            Impressions
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold">{ad.clicks}</div>
          <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <MousePointer className="w-3 h-3" />
            Clicks
          </div>
        </div>
      </div>

      {/* Dates */}
      {ad.start_date && (
        <div className="text-xs text-muted-foreground pt-3 border-t border-border">
          {format(parseISO(ad.start_date), "MMM d")} -{" "}
          {ad.end_date ? format(parseISO(ad.end_date), "MMM d, yyyy") : "Ongoing"}
        </div>
      )}
    </div>
  );
}
